import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import type { SearchResultItem } from '../../compound-components/model';
import { FloatingPanelComponent } from '../floating-panel/floating-panel.component';
import { PipelineEmbeddingsFeatureListComponent } from './pipeline-embeddings-feature-list.component';
import { PipelineEmbeddingsDetailDialogComponent } from './pipeline-embeddings-detail-dialog.component';
import { PipelineEmbeddingsPanelService, featureToEmbeddingSearchItem } from './pipeline-embeddings-panel.service';
import { MapInteractionService } from '../../ai-components/chat/services/map-interaction.service';
import { GeoService } from '../../viewer-components/services/internal-services/geo.service';

/**
 * Floating panel for pipeline embeddings results. Place inside a `position: relative` container.
 * Visibility driven by PipelineEmbeddingsPanelService.
 * Also handles opening individual-feature dialogs triggered by map clicks.
 */
@Component({
  selector: 'lib-pipeline-embeddings-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FloatingPanelComponent, PipelineEmbeddingsFeatureListComponent],
  template: `
    @if (panelService.panelOpen() && panelService.registeredLayer(); as layer) {
      <lib-floating-panel [title]="layer.layerName" (closed)="panelService.close()">
        <lib-pipeline-embeddings-feature-list
          [features]="layer.features"
          (navigateTo)="onNavigateTo($event)"
          (detail)="onDetail($event)" />
      </lib-floating-panel>
    }
  `,
})
export class PipelineEmbeddingsPanelComponent implements OnInit, OnDestroy {
  protected readonly panelService = inject(PipelineEmbeddingsPanelService);
  private readonly dialog = inject(MatDialog);
  private readonly mapInteraction = inject(MapInteractionService);
  private readonly subscriptions: Subscription[] = [];

  ngOnInit(): void {
    this.subscriptions.push(
      this.panelService.featureDetailTrigger$.subscribe((feature) => {
        const item = featureToEmbeddingSearchItem(feature);
        this.dialog.open(PipelineEmbeddingsDetailDialogComponent, {
          data: item,
          autoFocus: false,
        });
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  protected onNavigateTo(item: SearchResultItem): void {
    if (!item.geometry) return;
    try {
      const wgs84Feature = GeoService.wktPolygonToFeature(item.geometry);
      const [minX, minY, maxX, maxY] = GeoService.bbox(wgs84Feature.geometry);
      this.mapInteraction.zoomToExtent(minX, minY, maxX, maxY);
    } catch (err) {
      console.error('PipelineEmbeddingsPanelComponent: navigation failed', err);
    }
  }

  protected onDetail(item: SearchResultItem): void {
    this.dialog.open(PipelineEmbeddingsDetailDialogComponent, {
      data: item,
      autoFocus: false,
    });
  }
}
